<template>
    <LoadingCard v-if="loading" />
    <div class="content__inner" v-else>
        <header class="content__title pl-0">
            <h1>Add a weed spray diary entry
                <small class="pull-right" v-if="singleBlock">
                    <strong>Block:</strong> &ldquo;{{ singleBlock.name }}&rdquo; ({{ singleBlock.variety.name }})
                </small>
            </h1>

        </header>
        <form @submit.prevent="attemptCreate" ref="createForm" novalidate>

            <div class="card">
                <div class="card-header bg-primary text-white">
                    Required Details
                </div>

                <div class="card-body pt-3">

                    <!-- Blocks -->
                    <div class="form-group row" v-if="!singleBlock">
                        <label class="col-sm-2 col-form-label">Sprayed Blocks</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <VueSelect
                                        v-model="selectedBlocks"
                                        :options="blocks"
                                        :hide-selected="true"
                                        placeholder="Blocks..."
                                        label="name"
                                        track-by="id"
                                        :multiple="true"
                                >
                                </VueSelect>
                                <i class="form-group__bar"></i>
                                <div class="invalid-feedback" v-if="errors.has('block_id')">{{ errors.first('block_id') }}</div>

                            </div>
                        </div>
                    </div>

                    <!-- Spray Date -->
                    <div class="form-group row">
                        <label for="date" class="col-sm-2 col-form-label">Spray Date</label>
                        <div class="input-group col-sm-10">
                            <VueDatePicker
                                    id="date"
                                    v-model="date"
                                    :config="datePicker"
                                    class="form-control"
                                    :class="{'is-invalid': errors.has('date')}"
                                    placeholder="Select date"
                                    v-validate="validation.date"
                                    @on-change="updateFinishTime"
                                    name="date" />
                            <i class="form-group__bar"></i>
                            <div class="invalid-feedback" v-if="errors.has('date')">{{ errors.first('date') }}</div>
                        </div>
                    </div>

                    <!-- Applicator -->
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Applicator</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <VueSelect
                                        v-model="applicator"
                                        :options="growsafeUserData"
                                        placeholder="Applicator..."
                                        label="label"
                                        track-by="id"
                                        v-validate="validation.applicator"
                                        data-vv-name="growsafe_number"
                                        data-vv-as="growsafe number"
                                >
                                </VueSelect>
                                <i class="form-group__bar"></i>
                                <div class="invalid-feedback" v-if="errors.has('growsafe_number')">{{ errors.first('growsafe_number') }}</div>

                            </div>
                        </div>
                    </div>

                </div>

                <!-- Chemicals Used -->
                <div class="bg-light">
                    <h6 class="card-header text-secondary">Chemicals Used</h6>
                </div>

                <div class="card-body pt-1">
                    <b-table hover small stacked="md" :items="entryRows" :fields="entriesTable.fields" :show-empty="true">
                        <template slot="chemical" slot-scope="row">
                            <div class="position-relative">
                                <VueSelect
                                        v-b-tooltip="errors.first(`chemical_${row.index}`) || ''"
                                        @input="setRow('chemical', row.index, $event)"
                                        :value="row.item.chemical"
                                        v-validate="validation.entry_chemical"
                                        :data-vv-name="`chemical_${row.index}`"
                                        data-vv-as="chemical"
                                        :options="linkedChemicals"
                                        :allow-empty="false"
                                        :show-labels="false"
                                        placeholder="Chemical"
                                        label="name"
                                        track-by="id"
                                >
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="rate" slot-scope="row">
                            <div class="position-relative">
                                <VueMask :value="row.item.rate" class="form-control"
                                         v-b-tooltip="errors.first(`rate_${row.index}`) || ''"
                                         :class="{'is-invalid': errors.has(`rate_${row.index}`)}"
                                         placeholder="0..."
                                         v-validate="validation.entry_rate"
                                         :data-vv-name="`rate_${row.index}`"
                                         data-vv-as="rate"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                         @input="setRow('rate', row.index, $event)" required />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="weight" slot-scope="row">
                            <div class="position-relative">
                                <VueMask :value="row.item.weight" class="form-control"
                                         v-b-tooltip="errors.first(`weight_${row.index}`) || ''"
                                         :class="{'is-invalid': errors.has(`weight_${row.index}`)}"
                                         placeholder="0..."
                                         v-validate="validation.entry_weight"
                                         :data-vv-name="`weight_${row.index}`"
                                         data-vv-as="amount used"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                         @input="setRow('weight', row.index, $event)" />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="remove" slot-scope="row">
                            <b-button size="sm" variant="outline-danger" @click="removeRow(row.index)">Remove</b-button>
                        </template>
                    </b-table>

                    <div class="text-right">
                        <button type="button" class="btn btn-success" @click.prevent="addRow">Add Another Chemical</button>
                    </div>
                </div>


                <div class="bg-light">
                    <h6 class="card-header text-secondary">Fertilisers Used</h6>
                </div>
                <div class="card-body pt-1">
                    <b-table hover small stacked="md" :items="fertiliserEntryRows" :fields="fertiliserEntriesTable.fields" :show-empty="true">
                        <template slot="fertiliser" slot-scope="row">
                            <div class="position-relative">
                                <VueSelect
                                        v-b-tooltip="errors.first(`fertiliser_${row.index}`) || ''"
                                        @input="setFertiliserRow('fertiliser', row.index, $event)"
                                        :value="row.item.fertiliser"
                                        v-validate="fertiliserValidation.entry_fertiliser"
                                        :data-vv-name="`fertiliser_${row.index}`"
                                        data-vv-as="fertiliser"
                                        :options="linkedFertilisers"
                                        :allow-empty="false"
                                        :show-labels="false"
                                        placeholder="Fertiliser"
                                        label="name"
                                        track-by="id"
                                >
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="rate" slot-scope="row">
                            <div class="position-relative">
                                <VueMask :value="row.item.rate" class="form-control"
                                         v-b-tooltip="errors.first(`rate_${row.index}`) || ''"
                                         :class="{'is-invalid': errors.has(`rate_${row.index}`)}"
                                         placeholder="0..."
                                         v-validate="fertiliserValidation.entry_rate"
                                         :data-vv-name="`rate_${row.index}`"
                                         data-vv-as="rate"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                         @input="setFertiliserRow('rate', row.index, $event)" required />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="weight" slot-scope="row">
                            <div class="position-relative">
                                <VueMask :value="row.item.weight" class="form-control"
                                         v-b-tooltip="errors.first(`weight_${row.index}`) || ''"
                                         :class="{'is-invalid': errors.has(`weight_${row.index}`)}"
                                         placeholder="0..."
                                         v-validate="fertiliserValidation.entry_weight"
                                         :data-vv-name="`weight_${row.index}`"
                                         data-vv-as="amount used"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}"
                                         @input="setFertiliserRow('weight', row.index, $event)" />
                                <i class="form-group__bar"></i>
                            </div>
                        </template>
                        <template slot="remove" slot-scope="row">
                            <b-button size="sm" variant="outline-danger" @click="removeFertiliserRow(row.index)">Remove</b-button>
                        </template>
                    </b-table>

                    <div class="text-right">
                        <button type="button" class="btn btn-success" @click.prevent="addFertiliserRow">Add Another Fertiliser</button>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-header bg-primary text-white">
                    Optional Details
                </div>

                <div class="card-body pt-3">

                    <!-- Time Taken -->
                    <div class="form-group row">
                        <label for="end_time" class="col-sm-2 col-form-label">Spray Finish Time</label>
                        <div class="input-group col-sm-10">
                            <VueDatePicker
                                    id="end_time"
                                    v-model="end_time"
                                    :config="timePicker"
                                    class="form-control"
                                    placeholder="Select time"
                                    name="end_time" />
                            <i class="form-group__bar"></i>
                            <div class="input-group-prepend" v-if="end_time && date">
                                <span class="input-group-text">Time taken: {{ time_taken }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Wind Direction -->
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Wind Direction</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <VueSelect
                                        v-model="wind_direction"
                                        :options="windDirectionValues"
                                        :allow-empty="false"
                                        deselect-label=""
                                        placeholder="Wind Direction..."
                                        label="description"
                                        track-by="name"
                                >
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>

                    <!-- Wind Speed -->
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Wind Speed (km/h)</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <VueSelect
                                        v-model="wind_speed"
                                        :options="windSpeedValues"
                                        :allow-empty="false"
                                        deselect-label=""
                                        placeholder="Wind Speed..."
                                        label="description"
                                        track-by="name"
                                >
                                </VueSelect>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>

                    <!-- Cloud Cover -->
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Cloud Cover (percentage)</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <VueMask v-model.number="cloud_cover" class="form-control"
                                         :class="{'is-invalid': errors.has('cloud_cover')}"
                                         placeholder="0..."
                                         v-validate="validation.cloud_cover"
                                         data-vv-name="cloud_cover"
                                         data-vv-as="cloud cover"
                                         :options="{numeral: true, numeralDecimalScale: 0, numeralIntegerScale: 3}" />
                                <div class="invalid-feedback" v-if="errors.has('cloud_cover')">{{ errors.first('cloud_cover') }}</div>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>

                    <!-- Temperature -->
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Temperature (&deg;c)</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <VueMask v-model.number="temperature" class="form-control"
                                         :class="{'is-invalid': errors.has('temperature')}"
                                         placeholder="0..."
                                         v-validate="validation.temperature"
                                         data-vv-name="temperature"
                                         data-vv-as="temperature"
                                         :options="{numeral: true, numeralThousandsGroupStyle: 'thousand'}" />
                                <div class="invalid-feedback" v-if="errors.has('temperature')">{{ errors.first('temperature') }}</div>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>

                    <!-- Comments -->
                    <div class="form-group row">
                        <label class="col-sm-2 col-form-label">Comments</label>
                        <div class="col-sm-10">
                            <div class="position-relative">
                                <textarea class="form-control" v-model="comments" placeholder="Comments..." v-autosize></textarea>
                                <div class="invalid-feedback" v-if="errors.has('comments')">{{ errors.first('comments') }}</div>
                                <i class="form-group__bar"></i>
                            </div>
                        </div>
                    </div>

                    <div class="text-right">
                        <button type="submit" class="btn btn-success">Create</button>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import listWindDirectionValuesQuery from '@gql/enumValues/queryWindDirectionEnumValues.graphql';
import listWindSpeedValuesQuery from '@gql/enumValues/queryWindSpeedEnumValues.graphql';
import createSprayLogMutation from '@views/WeedSprayDiary/Create/mutationCreateSprayLog.graphql';
import currentUserQuery from '@views/WeedSprayDiary/Create/queryCurrentUser.graphql';
import listLinkedChemicalsQuery from '@views/WeedSprayDiary/Create/queryListGrowerLinkedChemicalCommercialVariants.graphql';
import listLinkedFertilisersQuery from '@views/WeedSprayDiary/Create/queryListGrowerLinkedFertilisers.graphql';
import listGrowsafeUsersQuery from '@views/WeedSprayDiary/Create/queryListGrowsafeUsers.graphql';
import differenceInMilliseconds from 'date-fns/difference_in_milliseconds';
import differenceInMinutes from 'date-fns/difference_in_minutes';

import ConfirmDatePlugin from 'flatpickr/dist/plugins/confirmDate/confirmDate';
import DifferenceFormatter from 'humanize-duration-es6';
import differenceFormatterLocaleEn from 'humanize-duration-es6/dist/locale/en';

const datePickerConfig = (config = {}) => {
  return {
    plugins: [new ConfirmDatePlugin({ confirmIcon: '' })],
    wrap: true, // set wrap to true only when using 'input-group'
    altInput: true,
    enableTime: true,
    nextArrow: '<i class="zmdi zmdi-long-arrow-right" />',
    prevArrow: '<i class="zmdi zmdi-long-arrow-left" />',
    ...config,
  };
};

export default {
  name: 'CreateWeedSprayDiary',
  props: ['blocks', 'userId'],
  apollo: {
    currentUser: {
      query: currentUserQuery,
      update: data => data.currentUser,
    },
    linkedChemicals: {
      query: listLinkedChemicalsQuery,
      update: data => data.filterGrowerChemicalCommercialVariantLinks,
      variables() {
        if (this.userId) {
          return {
            input: {
              user_id: this.userId,
            },
          };
        }
      },
    },
    linkedFertilisers: {
      query: listLinkedFertilisersQuery,
      update: data => data.filterGrowerFertiliserLinks,
      variables() {
        if (this.userId) {
          return {
            input: {
              user_id: this.userId,
            },
          };
        }
      },
    },
    growsafeUsers: {
      query: listGrowsafeUsersQuery,
      update: data => {
        return data.filterGrowsafeUsers.data;
      },
      variables() {
        return {
          input: {
            limit: 1000,
          },
        };
      },
    },
    windSpeedValues: {
      query: listWindSpeedValuesQuery,
      update: data => data.__type.enumValues,
    },
    windDirectionValues: {
      query: listWindDirectionValuesQuery,
      update: data => data.__type.enumValues,
    },
  },
  data() {
    return {
      loading: 0,
      currentUser: null,
      applicator: null,
      growsafeUsers: [],
      selectedBlocks: this.blocks.length === 1 ? this.blocks : [],
      date: null,
      end_time: null,
      wind_direction: null,
      wind_speed: null,
      cloud_cover: null,
      temperature: null,
      comments: null,
      windSpeedValues: [],
      windDirectionValues: [],
      fertiliserEntryRows: Array.from({ length: 3 }, () => ({
        fertiliser: null,
        rate: null,
        weight: null,
      })),
      entryRows: Array.from({ length: 3 }, () => ({
        chemical: null,
        rate: null,
        weight: null,
      })),
      linkedChemicals: [],
      linkedFertilisers: [],
      fertiliserEntriesTable: {
        fields: [
          { key: 'fertiliser', label: 'Fertiliser' },
          { key: 'rate', label: 'Rate(mls/gms per 100 lts)' },
          { key: 'weight', label: 'Amount Used(kgs or lts)' },
          { key: 'remove', label: 'Remove' },
        ],
      },
      entriesTable: {
        fields: [
          { key: 'chemical', label: 'Chemical' },
          { key: 'rate', label: 'Rate(mls/gms per 100 lts)' },
          { key: 'weight', label: 'Amount Used(kgs or lts)' },
          { key: 'remove', label: 'Remove' },
        ],
      },
      datePicker: datePickerConfig({
        altFormat: 'M j, Y at h:iK',
        dateFormat: 'Y-m-d H:i:S',
      }),
      timePicker: datePickerConfig({
        altFormat: 'h:iK',
        dateFormat: 'H:i',
        noCalendar: true,
      }),
      fertiliserValidation: {
        entry_fertiliser: { required: true },
        entry_rate: { required: true, min_value: 0 },
        entry_weight: { required: true, min_value: 0 },
      },
      validation: {
        entry_chemical: { required: true },
        date: { required: true },
        applicator: { required: true },
        entry_rate: { required: true, min_value: 0 },
        entry_weight: { required: true, min_value: 0 },
        cloud_cover: { min_value: 0 },
        temperature: {},
      },
    };
  },
  computed: {
    growsafeUserData() {
      return this.growsafeUsers.map(user => ({
        id: user.id,
        label: `${user.first_name} ${user.last_name} (${user.certificate_number})`,
        certificate_number: user.certificate_number,
      }));
    },
    singleBlock() {
      return this.blocks.length === 1 ? this.blocks[0] : null;
    },
    time_taken_in_minutes() {
      if(!this.end_time) return null;

      return differenceInMinutes(
        `${this.date.split(' ')[0]} ${this.end_time}:00`,
        this.date,
      );
    },
    time_taken() {
      if(!this.end_time) return null;

      const formatter = new DifferenceFormatter(
        differenceFormatterLocaleEn,
      );
      return formatter.humanize(
        differenceInMilliseconds(
          this.date,
          `${this.date.split(' ')[0]} ${this.end_time}:00`,
        ),
      );
    },
  },
  methods: {
    addRow() {
      this.entryRows.push({
        chemical: null,
        rate: null,
        weight: null,
      });
    },
    removeRow(index) {
      this.entryRows.splice(index, 1);
    },
    setRow(field, index, value) {
      this.entryRows[index][field] = value;
    },
    addFertiliserRow() {
      this.fertiliserEntryRows.push({
        fertiliser: null,
        rate: null,
        weight: null,
      });
    },
    removeFertiliserRow(index) {
      this.fertiliserEntryRows.splice(index, 1);
    },
    setFertiliserRow(field, index, value) {
      this.fertiliserEntryRows[index][field] = value;
    },
    updateFinishTime(_, startDate) {
      const time = (startDate.split(' ')[1] || '00:00:00').substr(0, 5);
      this.$set(this.timePicker, 'minDate', time);
    },
    attemptCreate() {
      this.errors.remove('global');
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (!validationWasSuccessful) {
          console.log('validation failed');
          return;
        }

        const mutationInput = {
          block_id: this.selectedBlocks.map(block => block.id),
          growsafe_number: this.applicator.certificate_number,
          sprayed_at: this.date,
          time_taken: this.time_taken_in_minutes,
          comments: this.comments,
          wind_direction: this.wind_direction ? this.wind_direction.name : null,
          wind_speed: this.wind_speed ? this.wind_speed.name : null,
          temperature: this.temperature,
          cloud_cover: this.cloud_cover,
          entries: this.entryRows.map(row => ({
            chemical_id: row.chemical.id,
            rate: row.rate,
            weight: row.weight,
          })),
          fertiliserEntries: this.fertiliserEntryRows.map(row => ({
            fertiliser_id: row.fertiliser.id,
            rate: row.rate,
            weight: row.weight,
          })),
        };

        console.log(mutationInput);

        this.$apollo
          .mutate({
            mutation: createSprayLogMutation,
            variables: {
              input: mutationInput,
            },
          })
          .then(() => {
            this.$notifySuccess('Weed Spray log created!');
            this.date = null;
            this.end_time = null;
            this.wind_direction = null;
            this.wind_speed = null;
            this.cloud_cover = null;
            this.temperature = null;
            this.applicator = null;
            this.comments = null;
            this.windSpeedValues = [];
            this.windDirectionValues = [];
            this.fertiliserEntryRows = Array.from({ length: 3 }, () => ({
              fertiliser: null,
              rate: null,
              weight: null,
            }));
            this.entryRows = Array.from({ length: 3 }, () => ({
              chemical: null,
              rate: null,
              weight: null,
            }));

            this.$validator.reset();
          })
          .catch(err => {this.$mapGraphQLError(err);})
          .catch(err => {
            console.error(err);
            this.$notifyFailure('Unfortunately, we were unable to do that!');
          });
      });
    },
  },
};
</script>
